import React, { useState } from "react";
import Highcharts from "highcharts";
import "highcharts/highcharts-more";
import { useSelector } from "react-redux";
import HighchartsReact from "highcharts-react-official";
import { ChevronDown, ThermometerSun, SlidersHorizontal } from "lucide-react";
import { generateDateTicks } from "../../../services/dataTicks";
import clsx from "clsx";

const fuelColors = ["#1a5276", "#27ae60", "#e74c3c", "#f39c12"];

const transformApiData = (apiData) => {
  console.log("EngineSFOCGraph API Data:", apiData);

  if (!apiData || !Array.isArray(apiData)) {
    return {
      leftAxisData: [],
      rightAxisData: [],
      leftAxisRange: [],
    };
  }

  return {
    leftAxisData: apiData.map((item) => ({
      date: item.date,
      value: item.sfoc,
    })),

    rightAxisData: apiData.map((item) => ({
      date: item.date,
      value: item.power,
    })),

    leftAxisRange: apiData.map((item) => ({
      date: item.date,
      min: item.sfoc > 0 ? item.sfoc - 15 : 0, // 15 units below actual
      max: item.sfoc > 0 ? item.sfoc + 15 : 0, // 15 units above actual
    })),
  };
};

const EngineSFOCGraph = ({ data = [] }) => {
  const [isOpen, setIsOpen] = useState(true);
  const [selectedMetrics, setSelectedMetrics] = useState([
    "SFOC",
    "Power"
  ]);

  const handleToggleMetric = (metric) => {
    setSelectedMetrics((prev) =>
      prev.includes(metric) 
        ? prev.filter((m) => m !== metric) 
        : [...prev, metric]
    );
  };

  const chartData = transformApiData(data);

  const dates = data ? data.map(item => item.date) : []

  let tickPositions = generateDateTicks(dates);

  // Convert date strings to timestamps for Highcharts
  const convertToTimestamp = (dateStr) => new Date(dateStr).getTime();

  // Filter series based on selected metrics
  const getSeries = () => {
    const series = [];

    if (selectedMetrics.includes("SFOC")) {
      series.push({
        name: "SFOC Actual",
        type: "spline",
        yAxis: 0, // Use left Y-axis
        data: chartData.leftAxisData.map(({ date, value }) => [
          convertToTimestamp(date),
          value,
        ]),
        color: fuelColors[0],
        lineWidth: 3,
        marker: {
          enabled: true,
          radius: 4,
          fillColor: fuelColors[0],
          lineColor: "#fff",
          lineWidth: 2,
        },
        tooltip: {
          valueSuffix: " g/kWh",
        },
      });
    }

    if (selectedMetrics.includes("Power")) {
      series.push({
        name: "Power Load",
        type: "spline",
        yAxis: 1, // Use right Y-axis
        data: chartData.rightAxisData.map(({ date, value }) => [
          convertToTimestamp(date),
          value,
        ]),
        color: fuelColors[1],
        lineWidth: 3,
        marker: {
          enabled: true,
          radius: 4,
          fillColor: fuelColors[1],
          lineColor: "#fff",
          lineWidth: 2,
        },
        tooltip: {
          valueSuffix: " %",
        },
      });
    }

    return series;
  };

  const options = {
    chart: {
      backgroundColor: "transparent",
      zoomType: "x",
      height: 440,
    },
    title: {
      text: null,
    },
    // X-axis with dates
    xAxis: {
      type: "datetime",
      tickPositions: tickPositions,
      title: { text: "Date", style: { color: "#0a2540" } },
      labels: {
        style: { color: "#0a2540" },
        format: "{value:%b %d}", // Format: Jan 01
      },
      gridLineColor: "#e2e8f0",
      dateTimeLabelFormats: {
        day: "%b %d",
        week: "%b %d",
        month: "%b %Y",
      },
    },

    // Left Y-axis
    yAxis: [
      {
        title: { text: "SFOC (g/kWh)", style: { color: fuelColors[0] } },
        labels: {
          style: { color: fuelColors[0] },
        },
        gridLineColor: "#f1f5f9",
        opposite: false, // Left side
      },
      {
        // Right Y-axis
        title: { text: "Power (%)", style: { color: fuelColors[1] } },
        labels: {
          style: { color: fuelColors[1] },
        },
        opposite: true, // Right side
        gridLineWidth: 0, // Hide grid lines for right axis
      },
    ],

    tooltip: {
      shared: true,
      crosshairs: true,
      backgroundColor: "#fff",
      borderColor: "#94a3b8",
      borderRadius: 8,
      style: { color: "#0a2540" },
      xDateFormat: "%B %d, %Y",
    },

    series: getSeries(),

    credits: { enabled: false },

    legend: {
      align: "center",
      verticalAlign: "bottom",
      itemStyle: { color: "#0a2540" },
      itemHoverStyle: { color: "#1e40af" },
    },

    plotOptions: {
      series: {
        states: {
          hover: {
            halo: {
              size: 5,
              opacity: 0.25,
            },
          },
        },
      },
    },
  };

  return (
    <div className="w-full overflow-hidden transition-all duration-300">
      
      {/* Metrics Filter Selector */}
      <div className="flex items-center gap-4 mb-4">
        <label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
          <SlidersHorizontal className="w-4 h-4 text-[#27ade3]" />
          Select Metrics:
        </label>
        <div className="flex flex-wrap items-center justify-between gap-2.5">
          {["SFOC", "Power"].map((metric) => {
            const isActive = selectedMetrics.includes(metric);
            const colorIndex = metric === "SFOC" ? 0 : 1;
            
            return (
              <button
                key={metric}
                className={`flex items-center px-2 py-1.5 rounded-md transition-all ${
                  isActive
                    ? "shadow-sm"
                    : "opacity-60 hover:opacity-100"
                }`}
                style={{
                  backgroundColor: isActive ? fuelColors[colorIndex] : "transparent",
                  color: isActive ? "#ffffff" : "#6b7280"
                }}
                onClick={() => handleToggleMetric(metric)}
              >
                <span
                  className={`text-[13px] font-medium ${
                    isActive ? "text-white" : "text-gray-500"
                  }`}
                >
                  {metric}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div
        className={clsx(
          "transition-all duration-500 ease-in-out overflow-hidden",
          isOpen ? "py-4 px-4" : "max-h-0 py-0 px-4"
        )}
      >
        {isOpen && (
          <div className="h-96">
            <HighchartsReact
              highcharts={Highcharts}
              options={options}
              containerProps={{ style: { height: "100%", width: "100%" } }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default EngineSFOCGraph;